<template>
	<BlockVisibilityObserver title="OUTCOMES">
		<section class="kova-v2-outcomes">
			<div class="kova-v2-outcomes__inner">
				<div class="kova-v2-outcomes__visual">
					<!-- Chart decoration (6426:54299-54306 + 6463:71535 / 6557:33006-33018): the grid
					     lines, the weight curve and its end dot — Figma's own vectors, composed into one
					     frame-sized SVG per breakpoint. -->
					<picture aria-hidden="true" class="kova-v2-outcomes__chart">
						<source height="422" media="(min-width: 1024px)" :srcset="chartDesktop" width="576" />
						<img alt="" decoding="async" height="248" loading="lazy" :src="chartMobile" width="355" />
					</picture>

					<div class="kova-v2-outcomes__stat">
						<span class="kova-v2-outcomes__stat-text">
							<span class="kova-v2-outcomes__stat-value">{{ stat.value }}</span>
							<span class="kova-v2-outcomes__stat-label">{{ stat.label }}</span>
						</span>
					</div>

					<img
						alt=""
						class="kova-v2-outcomes__photo"
						height="800"
						decoding="async"
						loading="lazy"
						:src="photo"
						width="964"
					/>
				</div>

				<div class="kova-v2-outcomes__copy">
					<h2 class="kova-v2-outcomes__title">{{ title }}</h2>
					<p class="kova-v2-outcomes__claim">
						<span
							v-for="(part, index) in claimParts"
							:key="index"
							:class="{ 'kova-v2-outcomes__claim-accent': part.accent }"
							v-text="part.text"
						/>
					</p>
					<p class="kova-v2-outcomes__footnote">{{ footnote }}</p>
				</div>
			</div>
		</section>
	</BlockVisibilityObserver>
</template>

<script setup lang="ts">
import BlockVisibilityObserver from '@/components/BlockVisibilityObserver.vue'
import { LandingComponentProps } from '@/modules/Landing/instances/@types/landing-types'

import chartDesktop from '@/assets/kova/landing/v2/outcomes-chart-desktop.svg'
import chartMobile from '@/assets/kova/landing/v2/outcomes-chart-mobile.svg'
import photo from '@/assets/kova/landing/v2/outcomes-clinician.webp'

defineOptions({ name: 'LandingKovaV2Outcomes' })

defineProps<LandingComponentProps.KovaV2Outcomes>()
</script>

<style lang="scss" scoped>
// Mobile (6426:54295): 20 top, 32 bottom, 20px gutters; visual, then copy 24px below.
.kova-v2-outcomes {
	font-family: var(--kova-lp-font);
}

.kova-v2-outcomes__inner {
	display: flex;
	flex-direction: column;
	gap: var(--size-24);
	padding: var(--size-20) var(--size-20) var(--size-32);
}

// 6426:54297 — 355x248 (5px wider than the 350 column, as in the frame), 36 radius, mint wash.
.kova-v2-outcomes__visual {
	position: relative;
	overflow: hidden;
	isolation: isolate;

	width: min(355px, calc(100% + 5px));
	height: 248px;

	border-radius: var(--size-36);
	background: linear-gradient(var(--kova-v2-outcome-tint), var(--kova-v2-outcome-tint)), var(--Fill-White);

	img {
		position: absolute;
		display: block;
		max-width: none;
	}
}

// The chart is drawn in the 355x248 frame's own coordinates, pinned to its top-left.
.kova-v2-outcomes__chart img {
	left: 0;
	top: 0;
	width: 355px;
	height: 248px;
}

// 6426:54310 — callout bubble at (251, bottom 121) of the 355 card, held from the right
// edge so it stays whole when the card narrows below 390.
//
// The bubble is Figma's own outline (Rectangle 106: 16-radius box with a 5px tail) as a
// clip-path on the #107A44 fill; its 10px background blur has nothing to blur on the
// opaque fill, so it is left out.
.kova-v2-outcomes__stat {
	position: absolute;
	right: 14.5px;
	bottom: 121px;
	z-index: 1;

	width: 89.5px;
	height: 62px;

	background: var(--kova-lp-highlight);
	clip-path: path(
		'M0 16C0 7.16343 7.16344 0 16 0H73.5C82.3366 0 89.5 7.16344 89.5 16V41C89.5 49.8365 82.3366 57 73.5 57H55.2064C52.0393 57 49.0006 58.252 46.7528 60.4831L45.2246 62L43.6964 60.4831C41.4486 58.252 38.4099 57 35.2428 57H16C7.16346 57 0 49.8365 0 41V16Z'
	);
}

// 6426:54312 — white text centred in a 73px column, 15px above the bubble's bottom.
.kova-v2-outcomes__stat-text {
	position: absolute;
	left: var(--size-8);
	bottom: 15px;

	display: flex;
	flex-direction: column;
	align-items: center;
	gap: var(--size-6);
	width: 73px;

	text-align: center;
	color: var(--Fill-White);
}

.kova-v2-outcomes__stat-value {
	font-size: var(--size-24);
	font-weight: 600;
	line-height: var(--size-14);
}

.kova-v2-outcomes__stat-label {
	font-size: var(--size-11); // 6426:54314
	font-weight: 500;
	line-height: var(--size-14);
	white-space: nowrap;
}

// 6463:71482 — 288x239, centred 45.5px left of the middle, 7px below the bottom.
.kova-v2-outcomes__photo {
	left: calc(50% - 45.5px);
	bottom: -7px;
	width: 288px;
	height: 239px;
	object-fit: cover;
	transform: translateX(-50%);
}

.kova-v2-outcomes__copy {
	display: flex;
	flex-direction: column;
	gap: var(--size-16);

	text-align: center;
}

// 6426:54296 — 28 / 1.16.
.kova-v2-outcomes__title {
	margin: 0;

	font-size: var(--size-28);
	font-weight: 500;
	line-height: var(--size-32); // 28px × 1.16 = 32.48px — exact size not in design system
	letter-spacing: -0.56px;

	color: var(--kova-lp-ink);
}

.kova-v2-outcomes__claim {
	margin: 0;

	font-size: var(--size-16);
	line-height: var(--size-22); // 16px × 1.4 = 22.40px — exact size not in design system

	color: var(--kova-lp-ink);
}

.kova-v2-outcomes__claim-accent {
	font-weight: 600;
	color: var(--kova-lp-highlight);
}

// 6463:71489 — Inter 12/16, 4px tighter to the claim (12 vs the 16 above).
.kova-v2-outcomes__footnote {
	margin: calc(-1 * var(--size-4)) 0 0;

	font-family: var(--font-family);
	font-size: var(--size-12);
	line-height: var(--size-16);

	color: var(--Text-Primary-40);
}

// Desktop (6557:32902): 60px block padding; 576x422 visual left, copy right, 48 apart.
@media (min-width: 1024px) {
	.kova-v2-outcomes__inner {
		box-sizing: border-box;
		flex-direction: row;
		align-items: flex-start;
		gap: var(--size-48);
		max-width: 1440px;
		margin-inline: auto;
		padding: var(--size-60) var(--size-40);
	}

	.kova-v2-outcomes__visual {
		flex: 0 1 576px;
		width: auto;
		min-width: 0;
		height: 422px;

		border-radius: var(--size-40);
	}

	.kova-v2-outcomes__chart img {
		width: 576px;
		height: 422px;
	}

	// 6557:33019 — bubble at (421, bottom 249).
	.kova-v2-outcomes__stat {
		left: 421px;
		right: auto;
		bottom: 249px;

		width: 113.5px;
		height: 74px;

		clip-path: path(
			'M0 16C0 7.16344 7.16344 0 16 0H97.5C106.337 0 113.5 7.16344 113.5 16V53C113.5 61.8365 106.337 69 97.5 69H67.2064C64.0393 69 61.0006 70.252 58.7528 72.4831L57.2246 74L55.6964 72.4831C53.4486 70.252 50.4099 69 47.2428 69H16C7.16346 69 0 61.8365 0 53V16Z'
		);
	}

	// 6557:33021 — top 107 of the visual, i.e. 8px into the 74px bubble at y 99.
	.kova-v2-outcomes__stat-text {
		left: var(--size-20);
		top: var(--size-8);
		bottom: auto;

		gap: var(--size-4);
	}

	.kova-v2-outcomes__stat-value {
		font-size: var(--size-32);
		line-height: var(--size-32);
	}

	.kova-v2-outcomes__stat-label {
		font-size: var(--size-14);
		line-height: var(--size-14);
	}

	// 6557:32984 — 482x400 bottom-left.
	.kova-v2-outcomes__photo {
		left: 0;
		bottom: 0;
		width: 482px;
		height: 400px;
		transform: none;
	}

	.kova-v2-outcomes__copy {
		flex: 1 1 0;
		gap: var(--size-24);
		min-width: min-content; // Keep full title words inside the column at the 1024px breakpoint.

		text-align: left;
	}

	.kova-v2-outcomes__title {
		font-size: var(--size-56);
		line-height: var(--size-60); // 56px × 1.1 = 61.60px — exact size not in design system
		letter-spacing: -1.12px;
	}

	.kova-v2-outcomes__claim {
		font-size: var(--size-24);
		line-height: var(--size-36);
	}

	.kova-v2-outcomes__footnote {
		margin: 0;

		font-size: var(--size-14);
		line-height: var(--size-20);
	}
}

@media (min-width: 1440px) {
	.kova-v2-outcomes__inner {
		padding-inline: var(--size-120);
	}
}
</style>
